import { ChevronDown } from "lucide-react";
import { Card } from "../ui/Card";
import { ProgressBar } from "../ui/ProgressBar";
import { cn } from "../../lib/cn";
import { dataQualityScore } from "../../screens/plan-utils";
import {
  buildDataQualityReasons,
  confidenceLabel,
  dataQualityDotClass,
  dataQualityTip,
  type EngineSnapshot,
} from "../progress/utils";

export interface PlanDataQualityCardProps {
  readonly snapshot: EngineSnapshot;
}

export function PlanDataQualityCard({ snapshot }: PlanDataQualityCardProps) {
  const score = dataQualityScore(snapshot.dataQuality);
  const reasons = buildDataQualityReasons(snapshot);
  const visibleReasons = reasons.slice(0, 2);

  return (
    <Card className="flex flex-col gap-3 p-4" data-testid="data-quality-card">
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-base font-semibold text-text">Plan data quality</h3>
        <span className="flex items-center gap-1.5 text-xs font-semibold text-text" data-testid="data-quality-label">
          <span aria-hidden className={cn("inline-block h-2 w-2 rounded-full", dataQualityDotClass(snapshot.dataQuality))} />
          {confidenceLabel(snapshot.dataQuality)}
        </span>
      </div>

      {score !== null ? (
        <div data-testid="data-quality-bar">
          <ProgressBar value={(score / 3) * 100} color="#2f855a" trackColor="#f7f3ea" height={8} />
        </div>
      ) : (
        <p className="text-xs leading-relaxed text-text-muted">
          Still learning — the quality meter appears after a few days of logs.
        </p>
      )}

      {visibleReasons.length > 0 && (
        <ul className="flex flex-col gap-1.5 text-xs leading-relaxed text-text-muted" data-testid="data-quality-flags">
          {visibleReasons.map((reason) => (
            <li key={reason} className="flex items-start gap-2">
              <span aria-hidden className="mt-1.5 inline-block h-1 w-1 shrink-0 rounded-full bg-text-muted/60" />
              {reason}
            </li>
          ))}
        </ul>
      )}

      {reasons.length > visibleReasons.length && (
        <details data-testid="data-quality-more">
          <summary className="flex min-h-[44px] cursor-pointer list-none items-center gap-1.5 text-xs font-semibold text-accent active:opacity-80">
            All quality factors
            <ChevronDown size={14} className="disclosure-chevron" aria-hidden />
          </summary>
          <ul className="flex flex-col gap-1.5 pb-1 text-xs leading-relaxed text-text-muted">
            {reasons.map((reason) => (
              <li key={reason} className="flex items-start gap-2">
                <span aria-hidden className="mt-1.5 inline-block h-1 w-1 shrink-0 rounded-full bg-text-muted/60" />
                {reason}
              </li>
            ))}
          </ul>
        </details>
      )}

      <p className="rounded-xl bg-accent-light/60 px-3 py-2 text-xs leading-relaxed text-accent-dark" data-testid="data-quality-tip">
        {dataQualityTip(snapshot)}
      </p>
    </Card>
  );
}
